import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

// ============================================
// ROLES
// ============================================
export const ROLES = {
  INNOVATION_GERENTE: 'innovation_gerente',
  ADMIN: 'admin',
  ALIANZAS_GERENTE: 'alianzas_gerente',
  ALIANZAS_COORDINADOR: 'alianzas_coordinador',
  ALIANZAS_AGENTE: 'alianzas_agente',
  KLOUD_OPS_GERENTE: 'kloud_ops_gerente',
  KLOUD_OPS_COORDINADOR: 'kloud_ops_coordinador',
  KLOUD_OPS_AGENTE: 'kloud_ops_agente',
  KLOUD_SAAS_GERENTE: 'kloud_saas_gerente',
  KLOUD_SAAS_COORDINADOR: 'kloud_saas_coordinador',
  KLOUD_SAAS_AGENTE: 'kloud_saas_agente',
  MESA_SPIN_GERENTE: 'mesa_spin_gerente',
  MESA_SPIN_COORDINADOR: 'mesa_spin_coordinador',
  MESA_SPIN_AGENTE: 'mesa_spin_agente',
  FINOPS_GERENTE: 'finops_gerente',
  FINOPS_COORDINADOR: 'finops_coordinador',
  FINOPS_ANALISTA: 'finops_analista',
  FINOPS_PRESUPUESTO: 'FINOPS_presupuesto',
  QA_GERENTE: 'qa_gerente',
  QA_COORDINADOR: 'qa_coordinador',
  QA_AGENTE: 'qa_agente',
  SERVICIOS_GERENTE: 'servicios_gerente',
  SERVICIOS_COORDINADOR: 'servicios_coordinador',
  SERVICIOS_AGENTE: 'servicios_agente',
  VENTAS_GERENTE: 'ventas_gerente',
  VENTAS_COORDINADOR: 'ventas_coordinador',
  VENTAS_AGENTE: 'ventas_agente',
  GOBIERNO_CLOUD_ADMIN: 'gobierno_cloud_admin',
  GOBIERNO_CLOUD_EDITOR: 'gobierno_cloud_editor',
  GOBIERNO_CLOUD_VIEWER: 'gobierno_cloud_viewer',
};

// ============================================
// AREAS (SOLO MÓDULOS PRINCIPALES)
// ============================================
export const AREAS = {
  ADMIN: 'admin',
  ALIANZAS: 'alianzas',
  KLOUD_OPS: 'kloud-ops',
  KLOUD_SAAS: 'kloud-saas',
  MESA_SPIN: 'mesa-spin',
  FINOPS: 'finops',
  QA: 'qa',
  SERVICIOS_PROFESIONALES: 'servicios-profesionales',
  VENTAS: 'ventas',
  GOBIERNO_CLOUD: 'gobierno-cloud',
  INNOVATION: 'innovation',
};

// ============================================
// MAPEO ROL → ÁREA
// ============================================
export const getAreaFromRole = (role) => {
  if (String(role).toLowerCase() === 'finops_presupuesto') return AREAS.FINOPS;
  if (role === ROLES.ADMIN) return AREAS.ADMIN;
  if (role.includes('alianzas')) return AREAS.ALIANZAS;
  if (role.includes('kloud_ops')) return AREAS.KLOUD_OPS;
  if (role.includes('kloud_saas')) return AREAS.KLOUD_SAAS;
  if (role.includes('mesa_spin')) return AREAS.MESA_SPIN;
  if (role.includes('finops')) return AREAS.FINOPS;
  if (role.includes('qa')) return AREAS.QA;
  if (role.includes('servicios')) return AREAS.SERVICIOS_PROFESIONALES;
  if (role.includes('ventas')) return AREAS.VENTAS;
  if (role.includes('gobierno_cloud')) return AREAS.GOBIERNO_CLOUD;
  if (role.includes('innovation')) return AREAS.INNOVATION;
  return null;
};

// ============================================
// LABELS
// ============================================
export const getAreaLabel = (area) => {
  const labels = {
    [AREAS.ADMIN]: 'Administración',
    [AREAS.ALIANZAS]: 'Alianzas',
    [AREAS.KLOUD_OPS]: 'Kloud Público OPS',
    [AREAS.KLOUD_SAAS]: 'Kloud Público SaaS',
    [AREAS.MESA_SPIN]: 'Mesa Spin',
    [AREAS.FINOPS]: 'FINOPS',
    [AREAS.QA]: 'QA',
    [AREAS.SERVICIOS_PROFESIONALES]: 'Servicios Profesionales',
    [AREAS.VENTAS]: 'Ventas',
    [AREAS.GOBIERNO_CLOUD]: 'Gobierno Cloud',
    [AREAS.INNOVATION]: 'Innovation',
  };
  return labels[area] || area;
};

// ============================================
// VIGENCIA DEL TOKEN
// ============================================
// El objeto `user` en localStorage no caduca; el JWT si (8 h). Sin esta
// verificacion la sesion parece viva y los endpoints autenticados dan 401.
const isTokenVigente = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch (err) {
    return false;
  }
};

// ============================================
// PROVIDER
// ============================================
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = () => {
      try {
        const savedUser = localStorage.getItem('user');

        if (savedUser && isTokenVigente()) {
          setUser(JSON.parse(savedUser));
        } else if (savedUser) {
          logout();
        }

      } catch (err) {
        console.error('Error loading user:', err);
        logout();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
  };

  // ============================================
  // ÁREAS DEL USUARIO
  // ============================================
  const getUserAreas = () => {
    if (!user) return [];
    if (!user.roles || user.roles.length === 0) return [];

    if (user.roles.includes(ROLES.ADMIN)) {
      return Object.values(AREAS);
    }

    const areas = new Set();
    user.roles.forEach(role => {
      const area = getAreaFromRole(role);
      if (area) areas.add(area);
    });

    return Array.from(areas);
  };

  // ============================================
  // PERMISOS POR ROL
  // ============================================
  const ROLE_PERMISSIONS = {
    [ROLES.FINOPS_PRESUPUESTO]: ['finops.presupuestos', 'finops.servicios_cloud'],
    [ROLES.ADMIN]: ['*'],

    // ALIANZAS
    [ROLES.ALIANZAS_GERENTE]: [
      'alianzas.qbr.view',
      'alianzas.qbr.gestion.view',
      'alianzas.qbr.gestion.create',
      'alianzas.qbr.gestion.edit',
      'alianzas.qbr.gestion.delete',
      'alianzas.seguimiento.view',
      'alianzas.seguimiento.create',
      'alianzas.seguimiento.edit',
      'alianzas.seguimiento.delete',
      'alianzas.comentarios.write',
      'alianzas.certificaciones.view',
      'alianzas.certificaciones.create',
      'alianzas.certificaciones.edit',
      'alianzas.certificaciones.delete',
    ],
    [ROLES.ALIANZAS_COORDINADOR]: [
      'alianzas.qbr.view',
      'alianzas.qbr.gestion.view',
      'alianzas.qbr.gestion.create',
      'alianzas.qbr.gestion.edit',
      'alianzas.seguimiento.view',
      'alianzas.seguimiento.create',
      'alianzas.seguimiento.edit',
      'alianzas.comentarios.write',
      'alianzas.certificaciones.view',
      'alianzas.certificaciones.create',
      'alianzas.certificaciones.edit',
    ],
    [ROLES.ALIANZAS_AGENTE]: [
      'alianzas.qbr.view',
      'alianzas.seguimiento.view',
      'alianzas.comentarios.write',
      'alianzas.certificaciones.view',
    ],

    // FINOPS
    [ROLES.FINOPS_GERENTE]: [
      'finops.servicios_cloud',
      'finops.dashboard',
      'finops.renovaciones',
      'finops.timesheets',
      'finops.calidad',
      'finops.billing'
    ],

    [ROLES.FINOPS_COORDINADOR]: [
      'finops.servicios_cloud',
      'finops.dashboard',
      'finops.renovaciones',
      'finops.timesheets'
    ],

    [ROLES.FINOPS_ANALISTA]: [
      'finops.servicios_cloud',
      'finops.dashboard'
    ],


    // KLOUD OPS
    [ROLES.KLOUD_OPS_GERENTE]: [
      'ops.dashboard.basic',
      'ops.dashboard.management',
      'ops.dashboard.fusion.view',
      'ops.inventario.view',
      'ops.horas.view',
      'ops.monitoreo.view',
      'ops.tickets-control.view',
      'ops.turnos.write',
      'ops.alertas.view',
    ],
    [ROLES.KLOUD_OPS_COORDINADOR]: [
      'ops.dashboard.basic',
      'ops.dashboard.management',
      'ops.dashboard.fusion.view',
      'ops.inventario.view',
      'ops.horas.view',
      'ops.monitoreo.view',
      'ops.tickets-control.view',
      'ops.turnos.write',
      'ops.alertas.view',
    ],
    [ROLES.KLOUD_OPS_AGENTE]: [
      'ops.dashboard.basic',
      'ops.inventario.view',
      'ops.horas.view',
      'ops.monitoreo.view',
      'ops.tickets-control.view',
      'ops.alertas.view',
    ],

    // VENTAS
    [ROLES.VENTAS_GERENTE]: [
      'ventas.dashboard',
      'ventas.agent.view',
      'ventas.agent.admin',
      'ventas.reports.view',
      'ventas.leads.view',
      'ventas.leads.manage',
    ],

    [ROLES.VENTAS_COORDINADOR]: [
      'ventas.dashboard',
      'ventas.agent.view',
      'ventas.reports.view',
      'ventas.leads.view',
    ],

    [ROLES.VENTAS_AGENTE]: [
      'ventas.dashboard',
      'ventas.agent.view',
    ],

    // GOBIERNO CLOUD
    [ROLES.GOBIERNO_CLOUD_ADMIN]: [
      'gobierno_cloud.cloud_accounts.view',
      'gobierno_cloud.cloud_accounts.create',
      'gobierno_cloud.cloud_accounts.edit',
      'gobierno_cloud.cloud_accounts.delete',
    ],
    [ROLES.GOBIERNO_CLOUD_EDITOR]: [
      'gobierno_cloud.cloud_accounts.view',
      'gobierno_cloud.cloud_accounts.create',
      'gobierno_cloud.cloud_accounts.edit',
    ],
    [ROLES.GOBIERNO_CLOUD_VIEWER]: [
      'gobierno_cloud.cloud_accounts.view',
    ],

    // (puedes extender aquí OPS, QA, etc.)
  };

  // ============================================
  // OBTENER PERMISOS DEL USUARIO
  // ============================================
  const getUserPermissions = () => {
    if (!user || !user.roles) return [];

    const permissions = new Set();

    user.roles.forEach(role => {
      const perms = ROLE_PERMISSIONS[role] || (String(role).toLowerCase() === 'finops_presupuesto' ? ['finops.presupuestos', 'finops.servicios_cloud'] : []);
      perms.forEach(p => permissions.add(p));
      if (role === ROLES.INNOVATION_GERENTE) {
        permissions.add('innovation.ai_models.manage');
      }
      if (role.toLowerCase().includes('innovation')) {
        permissions.add('innovation.dashboard.fusion.view');
      }
    });

    return Array.from(permissions);
  };

  // ============================================
  // VALIDAR PERMISO
  // ============================================
  const hasPermission = (permission) => {
    const perms = getUserPermissions();
    return perms.includes('*') || perms.includes(permission);
  };

  // ============================================
  // VALUE DEL CONTEXT
  // ============================================
  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    userAreas: getUserAreas(),
    hasAccessToArea: (area) => getUserAreas().includes(area),
    hasRoles: user && user.roles && user.roles.length > 0,
    permissions: getUserPermissions(),
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};